import { computed, ref } from 'vue';

/**
 * The loading bar across the top of the page, driven by whatever is in flight: route changes and
 * axios requests both start one and finish it when they are done (see `layouts/Base.vue`).
 *
 * Starts are reference counted, so overlapping work keeps the bar up until the last one finishes
 * rather than the first. Until then the bar trickles towards - but never reaches - the end, since
 * there is no way to know how much of a request is left; finishing the last task is what runs it
 * out to 100% and fades it away.
 */

/** Handed out by `start()`; call `finish()` once, further calls are ignored. */
export type ProgressFinisher = { finish: () => void };

/** How far the bar creeps per tick, slowing down the closer it gets to the end. */
const TRICKLE_MS = 300;
const FADE_MS = 500;

function trickleAmount(value: number): number {
	if (value < 20) return 10;
	if (value < 50) return 4;
	if (value < 80) return 2;
	if (value < 99) return 0.5;
	return 0;
}

const inflight = ref(0);
const value = ref(0);

let trickleTimer: ReturnType<typeof setInterval> | undefined;
let resetTimer: ReturnType<typeof setTimeout> | undefined;

const active = computed(() => inflight.value > 0);

function clearTimers() {
	if (trickleTimer) clearInterval(trickleTimer);
	if (resetTimer) clearTimeout(resetTimer);
	trickleTimer = undefined;
	resetTimer = undefined;
}

function start(): ProgressFinisher {
	if (inflight.value === 0) {
		clearTimers();
		value.value = 0;
		trickleTimer = setInterval(() => {
			value.value = Math.min(value.value + trickleAmount(value.value), 100);
		}, TRICKLE_MS);
	}
	inflight.value++;

	let used = false;
	return {
		finish: () => {
			if (used) return;
			used = true;
			inflight.value--;
			if (inflight.value > 0) return;

			// Run out to the end, then snap back to 0 once the bar has faded out, so the next
			// start() does not animate down from wherever this one stopped.
			clearTimers();
			value.value = 100;
			resetTimer = setTimeout(() => {
				value.value = 0;
			}, FADE_MS);
		},
	};
}

export function usePageProgress() {
	return { start, active, value };
}

export default usePageProgress;
